import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { can, canEditOwnDraft, canViewRecordAudit } from '../../lib/permissions';
import { api, ApiError } from '../../lib/api';
import { useDetail } from '../../lib/useDetail';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { KeyValue, LineTable, Pill, Spinner } from '../../components/ui';
import { contractStatus, dateLabel, money, num } from '../../lib/format';
import { CONTRACT_ACTIONS, type ContractAction } from '../../lib/sales';
import { openAuthenticatedPrint } from '../../lib/print';
import type { Contract, ContractLine, RecordAudit } from '../../lib/types';

function ActionError({ message }: { message: string }) {
  return <div className="notice bad" style={{ marginTop: 10 }}>{message}</div>;
}

// 交货进度条（合同级：已交数量 / 合同数量）。
function DeliveryProgress({ total, delivered }: { total: number; delivered: number }) {
  const pct = total > 0 ? Math.min(100, Math.round((delivered / total) * 100)) : 0;
  const remaining = Math.max(0, total - delivered);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      <div style={{ flex: '1 1 160px', minWidth: 140 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--ink-3)', marginBottom: 3 }}>
          <span>交货进度</span><span className="num">{pct}%</span>
        </div>
        <div style={{ height: 8, borderRadius: 6, background: 'var(--panel-inset)', overflow: 'hidden', border: '1px solid var(--line-soft)' }}>
          <div style={{ height: '100%', width: `${pct}%`, background: pct >= 100 ? 'var(--ok)' : 'var(--primary)' }} />
        </div>
      </div>
      <div className="num" style={{ fontSize: 12.5 }}>
        已交 <b>{num(delivered, 3)}</b> / {num(total, 3)} · 剩余 <b>{num(remaining, 3)}</b>
        {remaining <= 0 && total > 0 ? <Pill tone="ok" label="全部交货" /> : null}
      </div>
      <span className="muted" style={{ fontSize: 11.5 }}>剩余可交数量 = 合同数量 − 已交数量，交货单按行校验不可超交。</span>
    </div>
  );
}

export function ContractDetail({ row, onEdit, onChanged }: {
  row: Contract;
  onEdit: (c: Contract) => void;
  onChanged: () => void;
}) {
  const { user } = useAuth();
  const { data, loading, error, reload } = useDetail<Contract>(`/api/contracts/${row.id}`);
  const rec = data ?? row;
  const st = contractStatus(rec.status);
  const canWrite = can(user, 'contracts', 'create');
  const canPrint = can(user, 'contracts', 'print');
  const canAudit = canViewRecordAudit(user);
  const isOwnerDraft = rec.status === 'draft' && canEditOwnDraft(user, 'contracts', rec);
  const isLegacy = !!rec.legacy_erpnext_id;

  const [busy, setBusy] = useState(false);
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<ContractAction | null>(null);
  const [auditOpen, setAuditOpen] = useState(false);
  const [printing, setPrinting] = useState(false);
  const audit = useDetail<RecordAudit>(auditOpen ? `/api/audit/contracts/${row.id}` : null);

  const actions = CONTRACT_ACTIONS[rec.status] ?? [];
  const lines = rec.lines ?? [];
  const totalQty = lines.reduce((s, l) => s + Number(l.quantity), 0);
  const deliveredQty = lines.reduce((s, l) => s + Number(l.delivered_quantity || 0), 0);
  const hasDiscount = lines.some((l) => (l.discount_percentage ?? 0) > 0 || (l.discount_amount ?? 0) > 0 || (l.list_price ?? 0) > l.unit_price);

  const doTransition = async (action: ContractAction, reason: string) => {
    setBusy(true); setActionErr(null); setNotice(null);
    try {
      await api.post(`/api/contracts/${rec.id}/status`, { status: action.to, reason: reason || null });
      setNotice(`已变更为「${contractStatus(action.to).label}」，状态与操作人已写入审计。`);
      setConfirm(null);
      reload(); onChanged();
    } catch (e) {
      setActionErr(e instanceof ApiError ? e.message : '状态变更失败');
      setConfirm(null);
    } finally { setBusy(false); }
  };

  const doPrint = async () => {
    setPrinting(true); setActionErr(null);
    const r = await openAuthenticatedPrint('contract', rec.id);
    if (!r.ok) setActionErr(r.message || '打印失败');
    setPrinting(false);
  };

  // —— 主链：确认开工（自动建工单）→ 完成质检（完工入库/送修不入库）→ 交货/归还 ——
  // 与 QC 手机端同一套 /api/orders/*，办公室一键推进，不再分模块跳转。
  const [chainBusy, setChainBusy] = useState<string | null>(null);
  const [chainMsg, setChainMsg] = useState<string | null>(null);
  const isRepair = !!(rec as { is_repair?: boolean }).is_repair
    || ((rec as { related?: { work_orders?: { business_type?: string; service_cycle_id?: string | null }[] } }).related?.work_orders || [])
      .some((w) => String(w.business_type || '') === 'repair' || !!w.service_cycle_id);
  const runChain = async (step: 'confirm' | 'complete-qc' | 'deliver') => {
    setChainBusy(step); setChainMsg(null); setActionErr(null);
    try {
      if (step === 'confirm') {
        const r = await api.post(`/api/orders/${rec.id}/confirm`, isRepair ? { business_type: 'repair' } : {});
        const wos = (r as { work_orders?: { number?: string }[] }).work_orders
          || (r as { data?: { work_orders?: { number?: string }[] } }).data?.work_orders
          || [];
        setChainMsg(wos.length
          ? `已开工：工单 ${wos.map((w) => w.number).filter(Boolean).join('、')}${isRepair ? '（送修，不入库）' : ''}`
          : '订单已确认（工单幂等复用）');
      } else if (step === 'complete-qc') {
        const r = await api.post(`/api/orders/${rec.id}/complete-qc`, {
          result: 'accepted',
          inspector_name: user?.display_name || user?.username || '办公室',
          business_type: isRepair ? 'repair' : undefined,
        });
        const body = (r as { message?: string; data?: { message?: string } });
        setChainMsg(body.data?.message || body.message
          || (isRepair ? '质检完成（送修不入库）' : '质检完成，工单已完工入库'));
      } else {
        // 送修：归还客户（不扣成品库存）；自有：按行出库
        const remainingLines = lines
          .map((l) => ({ item_id: l.item_id, quantity: Number(l.quantity) - Number(l.delivered_quantity || 0) }))
          .filter((l) => l.quantity > 0);
        if (!isRepair && !remainingLines.length) throw new Error('没有可交数量，请先完成质检或检查是否已全部交货');
        const r = await api.post(`/api/orders/${rec.id}/deliveries`, {
          delivery_date: new Date().toISOString().slice(0, 10),
          ...(isRepair ? { handling: 'customer_return', return_to_customer: true } : {}),
          lines: isRepair
            ? lines.map((l) => ({ item_id: l.item_id, quantity: Number(l.quantity) || 1 }))
            : remainingLines,
        });
        const body = (r as {
          message?: string;
          delivery?: { number?: string };
          data?: { message?: string; delivery?: { number?: string } };
        });
        const deliveryNo = body?.delivery?.number || body?.data?.delivery?.number;
        setChainMsg(deliveryNo
          ? `交货单已生成：${deliveryNo}${isRepair ? '（归还，不扣库存）' : ''}`
          : body?.data?.message || body?.message || (isRepair ? '工件已归还客户' : '交货完成'));
      }
      reload(); onChanged();
    } catch (e) {
      setActionErr(e instanceof ApiError ? e.message : '主链操作失败');
    } finally { setChainBusy(null); }
  };
  const firstInspId = (rec as { inspections?: { id?: string }[] }).inspections?.[0]?.id;

  // 关联单据（从 getContract.related 汇总）
  const related = (rec as {
    related?: {
      quotation?: { id: string; number?: string | null } | null;
      work_orders?: { id: string; number: string; status?: string; business_type?: string }[];
      deliveries?: { id: string; number: string; status?: string }[];
      inspections?: { id: string; number: string; result?: string }[];
      boms?: { id: string; number: string }[];
      invoices?: { id: string; number: string }[];
    };
  }).related || {};
  const woList = related.work_orders || [];
  const dnList = related.deliveries || [];
  const qiList = related.inspections || [];
  const bomList = related.boms || [];
  const invList = related.invoices || [];
  const quoteNo = related.quotation?.number || rec.quotation_number;
  const firstInsp = firstInspId || qiList[0]?.id;

  const [metaBusy, setMetaBusy] = useState(false);
  const [metaMsg, setMetaMsg] = useState<string | null>(null);
  const [metaPo, setMetaPo] = useState(rec.po_no || '');
  const [metaDue, setMetaDue] = useState((rec.delivery_due_date || '').slice(0, 10));
  const [metaTerms, setMetaTerms] = useState(rec.terms_es || '');
  const [termsOpen, setTermsOpen] = useState(false);
  const saveMeta = async () => {
    setMetaBusy(true); setMetaMsg(null); setActionErr(null);
    try {
      await api.patch(`/api/contracts/${rec.id}`, {
        po_no: metaPo,
        delivery_due_date: metaDue || null,
        terms_es: metaTerms,
      });
      setMetaMsg('已保存（客户PO/交货日期/条款可交货后补录）');
      reload(); onChanged();
    } catch (e) {
      setActionErr(e instanceof ApiError ? e.message : '保存失败');
    } finally { setMetaBusy(false); }
  };

  return (
    <div className="detail" onClick={(e) => e.stopPropagation()}>
      <div className="detail-head">
        <span className="id">{rec.number}</span>
        <h3>{rec.customer_name || '客户'}</h3>
        <Pill tone={st.tone} label={st.label} />
        {isLegacy ? <span className="pill neutral">ERPNext 历史</span> : null}
        <div className="spacer" />
        {loading ? <Spinner label="加载明细…" /> : null}
      </div>
      {error ? <div className="notice bad">{error}</div> : null}
      {notice ? <div className="notice info">{notice}</div> : null}
      {actionErr ? <ActionError message={actionErr} /> : null}

      {/* 关联单据栏：点击进入对应列表/单据；多单显示计数 */}
      <div className="panel" style={{ boxShadow: 'none', padding: '10px var(--pad)' }}>
        <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13 }}>关联单据</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <Link className="btn" to={`/quotations?q=${encodeURIComponent(quoteNo || rec.number)}`}>
            报价单{quoteNo ? ` · ${quoteNo}` : related.quotation ? ' · 1' : ' · —'}
          </Link>
          <Link className="btn" to={`/work-orders?q=${encodeURIComponent(rec.number)}`}>
            工单{woList.length ? ` · ${woList.length}` : ''}
          </Link>
          <Link className="btn" to={`/boms?q=${encodeURIComponent(bomList[0]?.number || rec.number)}`}>
            物料清单{bomList.length ? ` · ${bomList.length}` : ''}
          </Link>
          <Link className="btn" to={`/deliveries?q=${encodeURIComponent(rec.number)}`}>
            交货单{dnList.length ? ` · ${dnList.length}` : ''}
          </Link>
          <Link className="btn" to={`/inspections?q=${encodeURIComponent(rec.number)}`}>
            质检单{qiList.length ? ` · ${qiList.length}` : ''}
          </Link>
          <span className="btn" style={{ opacity: 0.55, cursor: 'default' }} title="后期从 factura 回读下载">
            发票{invList.length ? ` · ${invList.length}` : ' · 暂未接入'}
          </span>
        </div>
        {(woList.length || dnList.length || qiList.length) ? (
          <div className="muted" style={{ fontSize: 11.5, marginTop: 8, lineHeight: 1.7 }}>
            {woList.length ? <div>工单：{woList.slice(0, 6).map((w, i) => (
              <span key={w.id}>{i ? '、' : ''}<Link to={`/work-orders?q=${encodeURIComponent(w.number)}`}>{w.number}</Link></span>
            ))}{woList.length > 6 ? ` 等 ${woList.length} 张` : ''}</div> : null}
            {dnList.length ? <div>交货：{dnList.slice(0, 6).map((d, i) => (
              <span key={d.id}>{i ? '、' : ''}<Link to={`/deliveries?q=${encodeURIComponent(d.number)}`}>{d.number}</Link></span>
            ))}{dnList.length > 6 ? ` 等 ${dnList.length} 张` : ''}</div> : null}
            {qiList.length ? <div>质检：{qiList.slice(0, 6).map((q, i) => (
              <span key={q.id}>{i ? '、' : ''}<Link to={`/inspections?q=${encodeURIComponent(q.number)}`}>{q.number}</Link></span>
            ))}{qiList.length > 6 ? ` 等 ${qiList.length} 张` : ''}</div> : null}
          </div>
        ) : null}
      </div>

      <div className="kv-grid">
        <KeyValue k="客户编码">{rec.customer_code || '—'}</KeyValue>
        <KeyValue k="客户税号">{rec.customer_tax_id || <span className="muted">资料待补</span>}</KeyValue>
        <KeyValue k="收货/开票地址">
          {rec.customer_address
            ? <span>{rec.customer_address}{[rec.customer_city, rec.customer_province, rec.customer_postal_code].filter(Boolean).length ? ` — ${[rec.customer_city, rec.customer_province, rec.customer_postal_code].filter(Boolean).join(' · ')}` : ''}</span>
            : <span className="muted">资料待补</span>}
        </KeyValue>
        <KeyValue k="客户 IVA 身份">{rec.customer_tax_category || <span className="muted">资料待补</span>}</KeyValue>
        <KeyValue k="客户 PO">{rec.po_no || <span className="muted">未填写</span>}</KeyValue>
        <KeyValue k="合同日期">{dateLabel(rec.contract_date)}</KeyValue>
        <KeyValue k="交付期限">{dateLabel(rec.delivery_due_date)}</KeyValue>
        <KeyValue k="币种">{rec.currency}</KeyValue>
        <KeyValue k="净额">{money(rec.subtotal, rec.currency)}</KeyValue>
        <KeyValue k="IVA 税额">{money(rec.tax_total, rec.currency)}</KeyValue>
        <KeyValue k="含税总额">{money(rec.grand_total, rec.currency)}</KeyValue>
        <KeyValue k="来源报价单">
          {rec.quotation_id
            ? (rec.quotation_number
              ? <Link to={`/quotations?q=${encodeURIComponent(rec.quotation_number)}`}>{rec.quotation_number}</Link>
              : '（已转合同）')
            : (isLegacy ? 'ERPNext 直接导入' : '合同手工新建')}
        </KeyValue>
      </div>

      {rec.quotation_id ? (
        <div className="notice" style={{ background: 'var(--panel-inset)' }}>
          本合同由报价单快照生成：行描述 / 数量 / 净单价 / IVA 已固化，后续报价修改不影响本合同。
        </div>
      ) : null}

      <div className="panel" style={{ boxShadow: 'none', padding: '12px var(--pad)' }}>
        <DeliveryProgress total={totalQty} delivered={deliveredQty} />
      </div>

      {!isLegacy && canWrite ? (
        <div className="panel" style={{ boxShadow: 'none', padding: '12px var(--pad)' }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>
            主链（与 QC 手机同一套）{isRepair ? ' · 送修不入库' : ''}
          </div>
          <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
            {isRepair
              ? '确认开工（建维修工单）→ 完成质检（只记履历、不入库）→ 归还交货 → 西语质检单'
              : '确认开工（按 BOM 自动建工单）→ 完成质检（自动领料·完工入库）→ 交货出库 → 西语质检单'}
          </div>
          {qiList.length ? (
            <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
              质检：{qiList.map((q) => `${q.number}${q.result && q.result !== 'pending' ? `（${q.result === 'accepted' ? '已完成' : q.result}）` : '（进行中）'}`).join('、')}
            </div>
          ) : null}
          {/* 主链进度：确认 / 工单 / 质检 / 交货 四步勾选 */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10, fontSize: 12 }}>
            {[
              { ok: rec.status !== 'draft', label: '已确认' },
              { ok: woList.length > 0 && woList.every((w) => ['completed', 'cancelled', 'voided'].includes(String(w.status || ''))), label: '工单完成' },
              { ok: qiList.length > 0 && qiList.every((q) => q.result && q.result !== 'pending'), label: '质检完成' },
              { ok: deliveredQty >= totalQty && totalQty > 0, label: '交货完成' },
            ].map((s) => (
              <span key={s.label} className={`pill ${s.ok ? 'ok' : 'neutral'}`} style={{ opacity: s.ok ? 1 : 0.65 }}>
                {s.ok ? '✓' : '○'} {s.label}
              </span>
            ))}
          </div>
          {chainMsg ? <div className="notice ok" style={{ marginBottom: 8 }}>{chainMsg}</div> : null}
          <div className="detail-actions" style={{ marginTop: 0 }}>
            <button className="btn primary" disabled={!!chainBusy || rec.status === 'draft'}
              title={rec.status === 'draft' ? '请先将合同流转到已确认/生产中' : '按已批准 BOM 自动建工单'}
              onClick={() => void runChain('confirm')}>
              {chainBusy === 'confirm' ? '开工中…' : '① 确认开工（建工单）'}
            </button>
            <button className="btn" disabled={!!chainBusy}
              onClick={() => void runChain('complete-qc')}>
              {chainBusy === 'complete-qc' ? '处理中…' : (isRepair ? '② 完成质检（送修不入库）' : '② 完成质检（完工入库）')}
            </button>
            <button className="btn" disabled={!!chainBusy}
              onClick={() => void runChain('deliver')}>
              {chainBusy === 'deliver' ? '交货中…' : (isRepair ? '③ 归还交货（不扣库存）' : '③ 交货出库')}
            </button>
            {firstInsp ? (
              <button className="btn" onClick={() => void openAuthenticatedPrint('inspection', firstInsp)}>
                西语质检单
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {!isLegacy ? (
        <div className="panel" style={{ boxShadow: 'none', padding: '12px var(--pad)' }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>打印抬头补录（交货后仍可改）</div>
          <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
            客户订单号（Pedido de Cliente）常在交货后由客户给出用于开票；条款无内容则打印不显示。
          </div>
          {metaMsg ? <div className="notice ok" style={{ marginBottom: 8 }}>{metaMsg}</div> : null}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8, marginBottom: 8, alignItems: 'end' }}>
            <label style={{ fontSize: 12 }}>客户 PO
              <input className="input" value={metaPo} onChange={(e) => setMetaPo(e.target.value)} placeholder="Pedido de Cliente" />
            </label>
            <label style={{ fontSize: 12 }}>交货日期
              <input className="input" type="date" value={metaDue} onChange={(e) => setMetaDue(e.target.value)} />
            </label>
            <button className="btn" type="button" onClick={() => setTermsOpen((v) => !v)}>
              {termsOpen ? '收起条款' : (metaTerms ? '编辑条款' : '添加条款')}
            </button>
          </div>
          {termsOpen ? (
            <div style={{ marginBottom: 8 }}>
              <label style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                西语条款 Términos y condiciones（可空；打印时无内容则不显示）
              </label>
              <textarea
                className="input"
                rows={5}
                style={{ width: '100%', minHeight: 100, fontFamily: 'inherit' }}
                value={metaTerms}
                onChange={(e) => setMetaTerms(e.target.value)}
                placeholder={'例如：\nEntrega: 15 días hábiles.\nPago: 50% anticipo, 50% contra entrega.\nValidez de la oferta: 30 días.'}
              />
            </div>
          ) : metaTerms ? (
            <div className="muted" style={{ fontSize: 12, marginBottom: 8, whiteSpace: 'pre-wrap' }}>{metaTerms}</div>
          ) : null}
          <button className="btn" disabled={metaBusy} onClick={() => void saveMeta()}>
            {metaBusy ? '保存中…' : '保存抬头'}
          </button>
        </div>
      ) : null}

      {rec.terms_es && !termsOpen && !metaMsg ? (
        <div className="notice" style={{ background: 'var(--panel-inset)', whiteSpace: 'pre-wrap' }}>西语条款：{rec.terms_es}</div>
      ) : null}

      <LineTable<ContractLine>
        rows={lines}
        columns={[
          { header: '编码', render: (l) => <span className="num muted">{l.item_code || '—'}</span> },
          { header: '描述', render: (l) => l.description_es },
          { header: '数量', align: 'right', render: (l) => <span className="num">{num(l.quantity, 3)}</span> },
          { header: '已交', align: 'right', render: (l) => <span className="num muted">{num(l.delivered_quantity, 3)}</span> },
          { header: '剩余可交', align: 'right', render: (l) => <span className="num strong">{num(Math.max(0, Number(l.quantity) - Number(l.delivered_quantity || 0)), 3)}</span> },
          { header: '单位', render: (l) => <span className="muted">{l.uom}</span> },
          ...(hasDiscount ? [
            { header: '原价', align: 'right' as const, render: (l: ContractLine) => <span className="num muted">{l.list_price ? money(l.list_price, rec.currency) : '—'}</span> },
            { header: '折扣', align: 'right' as const, render: (l: ContractLine) => <span className="num muted">{(l.discount_percentage ?? 0) > 0 ? `${num(l.discount_percentage, 0)}%` : (l.discount_amount ?? 0) > 0 ? money(l.discount_amount, rec.currency) : '—'}</span> },
          ] : []),
          { header: '净单价', align: 'right', render: (l) => <span className="num">{money(l.unit_price, rec.currency)}</span> },
          { header: 'IVA', align: 'right', render: (l) => <span className="num">{num(l.tax_rate, 0)}%</span> },
          { header: '行金额', align: 'right', render: (l) => <span className="num strong">{money(l.line_total, rec.currency)}</span> },
        ]}
      />

      {canWrite ? (
        <div className="detail-actions" style={{ flexWrap: 'wrap', marginTop: 4 }}>
          {canWrite && !isLegacy && rec.status !== 'cancelled' ? (
            <button className="btn primary" disabled={busy} onClick={() => onEdit(rec)}>
              {isOwnerDraft ? '编辑草稿' : '编辑明细（可换产品/改价）'}
            </button>
          ) : null}
          {actions.map((a) => (
            <button
              key={a.to}
              className={`btn ${a.primary ? 'primary' : ''}`}
              disabled={busy}
              title={a.adminOnly ? '该流转需管理员权限（点击后由服务端校验）' : undefined}
              onClick={() => { setActionErr(null); if (a.confirm) setConfirm(a); else void doTransition(a, ''); }}
            >
              {a.label}{a.adminOnly && user?.role !== 'admin' ? '（需管理员）' : ''}
            </button>
          ))}
          <div className="spacer" />
          {canPrint ? <button className="btn" disabled={printing} onClick={() => void doPrint()}>{printing ? '准备打印…' : '西语打印 / PDF'}</button> : null}
          {canAudit ? <button className="btn link" onClick={() => setAuditOpen((v) => !v)}>{auditOpen ? '收起审计' : '审计记录'}</button> : null}
        </div>
      ) : (
        <div className="detail-actions">
          {canPrint ? <button className="btn" disabled={printing} onClick={() => void doPrint()}>{printing ? '准备打印…' : '西语打印 / PDF'}</button> : null}
          {canAudit ? <button className="btn link" onClick={() => setAuditOpen((v) => !v)}>{auditOpen ? '收起审计' : '审计记录'}</button> : null}
          <span className="muted" style={{ fontSize: 12 }}>当前角色只读（写权限：销售 / 管理员）。</span>
        </div>
      )}

      {auditOpen ? (
        <div className="panel" style={{ boxShadow: 'none' }}>
          <div className="panel-body">
            {audit.loading ? <Spinner label="读取审计留痕…" /> : null}
            {audit.error ? <div className="notice bad">{audit.error}</div> : null}
            {audit.data ? (
              <>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>状态流转</div>
                {audit.data.status.length === 0 ? <div className="muted" style={{ fontSize: 12.5 }}>暂无状态变更记录。</div> : (
                  <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {audit.data.status.map((s) => {
                      const from = s.from_status ? contractStatus(s.from_status).label : '创建';
                      const to = contractStatus(s.to_status).label;
                      return (
                        <li key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
                          <Pill tone={contractStatus(s.to_status).tone} label={to} />
                          <span className="muted">{from} → {to}</span>
                          {s.reason ? <span>· {s.reason}</span> : null}
                          <span className="spacer" style={{ marginLeft: 'auto', fontSize: 11 }}>
                            {s.changed_by_name || '系统'} · {new Date(s.created_at).toLocaleString('zh-CN')}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>操作留痕（含改前 / 改后）</div>
                {audit.data.audit.length === 0 ? <div className="muted" style={{ fontSize: 12.5 }}>暂无操作日志。历史导入合同可能仅有导入留痕。</div> : (
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {audit.data.audit.map((a) => (
                      <li key={a.id} style={{ fontSize: 12.5, borderBottom: '1px solid var(--line-soft)', paddingBottom: 6 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span className="num strong">{a.action}</span>
                          <span className="muted" style={{ marginLeft: 'auto', fontSize: 11 }}>
                            {a.operator_name || '内部服务'} · {new Date(a.created_at).toLocaleString('zh-CN')}
                          </span>
                        </div>
                        {a.payload ? <div className="muted" style={{ fontSize: 11.5, marginTop: 2, wordBreak: 'break-all' }}>
                          {JSON.stringify(a.payload)}
                        </div> : null}
                      </li>
                    ))}
                  </ul>
                )}
              </>
            ) : null}
          </div>
        </div>
      ) : null}

      {confirm ? (
        <ConfirmDialog
          open
          title={`${confirm.label} · ${rec.number}`}
          message={`确认将销售合同「${rec.number}」变更为「${contractStatus(confirm.to).label}」？`}
          detail="该操作会写入操作人、时间与改前/改后状态，并由服务端强制校验流转合法性与角色权限（是否已全部交货 / 是否已有交货单）。"
          confirmLabel={confirm.label}
          tone={confirm.tone === 'bad' ? 'danger' : 'primary'}
          requireReason={confirm.to === 'cancelled'}
          reasonPlaceholder="例如：客户取消订单、重复签约…"
          busy={busy}
          onConfirm={(reason) => void doTransition(confirm, reason)}
          onCancel={() => setConfirm(null)}
        />
      ) : null}
    </div>
  );
}
